import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'

/**
 * El estado compartible de la pantalla de agendamiento, en la query string.
 *
 * El filtro que se usa a diario es el dia de ruta: "los del martes". Vive en la
 * url para que ese enlace se pueda pegar en el chat del equipo, junto con la
 * semana, la busqueda y la pagina.
 *
 * Lo que **no** entra: la lista pegada del modo masivo ni las casillas marcadas.
 * Son decenas de lineas y no caben en una url; ademas, un enlace que
 * reconstruyera veinte clientes listos para publicar seria un enlace peligroso.
 * Lo que se comparte es el contexto -que dia, que semana, que busqueda- y las
 * casillas las marca una persona.
 */

/**
 * La tabla es el modo por defecto: se elige el dia, se ven los clientes de ese
 * dia y se marcan. Los otros dos son atajos para quien ya trabaja de otra forma.
 */
export type Modo = 'tabla' | 'individual' | 'masivo'

const MODOS: readonly Modo[] = ['tabla', 'individual', 'masivo']

const MODO_POR_DEFECTO: Modo = 'tabla'

/** `r1`=lunes … `r5`=viernes. Es la traduccion que hace el backend. */
export type DiaRuta = 'r1' | 'r2' | 'r3' | 'r4' | 'r5'
export type SemanaReparto = 's1' | 's2' | 's3' | 's4'

const RUTAS: readonly DiaRuta[] = ['r1', 'r2', 'r3', 'r4', 'r5']
const SEMANAS_VALIDAS: readonly SemanaReparto[] = ['s1', 's2', 's3', 's4']

export interface FiltrosAgendamiento {
  modo: Modo
  /** Dia del reparto en ISO. Vacio mientras nadie lo haya elegido. */
  fecha: string
  /** Busqueda por nombre o NIF. Vale para la tabla y para el modo individual. */
  q: string
  /** `contact_id` del cliente elegido en el modo individual. */
  cliente: string
  /** Dia de ruta por el que filtra la tabla. Vacio es "todos los dias". */
  ruta: DiaRuta | ''
  semana: SemanaReparto | ''
  page: number
}

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * Una fecha inventada en la url se ignora en vez de viajar a la api. No basta
 * con la forma: `2026-13-45` la cumple y no existe, y el boton de publicar
 * escribe esa fecha delante del administrador.
 */
function leerFecha(valor: string | null): string {
  const partes = ISO.exec(valor ?? '')
  if (!partes) return ''
  const [, ano, mes, dia] = partes
  const fecha = new Date(Number(ano), Number(mes) - 1, Number(dia))
  const existe =
    fecha.getFullYear() === Number(ano) &&
    fecha.getMonth() === Number(mes) - 1 &&
    fecha.getDate() === Number(dia)
  return existe ? (valor as string) : ''
}

function leerModo(valor: string | null): Modo {
  return MODOS.find((modo) => modo === valor) ?? MODO_POR_DEFECTO
}

/**
 * `r10` no es una ruta y `s9` no es una semana: el backend los rechaza con un
 * 422. Se ignoran aqui para que un enlace mal copiado ensene la tabla entera en
 * vez de un error.
 */
function leerRuta(valor: string | null): DiaRuta | '' {
  return RUTAS.find((ruta) => ruta === valor) ?? ''
}

function leerSemana(valor: string | null): SemanaReparto | '' {
  return SEMANAS_VALIDAS.find((semana) => semana === valor) ?? ''
}

function leerPagina(valor: string | null): number {
  const numero = Number.parseInt(valor ?? '', 10)
  return Number.isFinite(numero) && numero >= 1 ? numero : 1
}

export function useFiltrosAgendamiento() {
  const [parametros, setParametros] = useSearchParams()

  const filtros = useMemo<FiltrosAgendamiento>(
    () => ({
      modo: leerModo(parametros.get('modo')),
      fecha: leerFecha(parametros.get('fecha')),
      q: parametros.get('q') ?? '',
      cliente: parametros.get('cliente') ?? '',
      ruta: leerRuta(parametros.get('ruta')),
      semana: leerSemana(parametros.get('semana')),
      page: leerPagina(parametros.get('page')),
    }),
    [parametros],
  )

  const escribir = useCallback(
    (cambios: Record<string, string | undefined>, reemplazar: boolean) => {
      const siguientes = new URLSearchParams(parametros)
      for (const [clave, valor] of Object.entries(cambios)) {
        if (valor) siguientes.set(clave, valor)
        else siguientes.delete(clave)
      }
      setParametros(siguientes, { replace: reemplazar })
    },
    [parametros, setParametros],
  )

  const cambiarModo = useCallback(
    (modo: Modo) => {
      // El cliente y la busqueda son del modo individual. Arrastrarlos al
      // masivo dejaria un cliente elegido invisible que se colaria al publicar.
      // El modo por defecto no se escribe: la url corta es la de siempre.
      escribir(
        {
          modo: modo === MODO_POR_DEFECTO ? undefined : modo,
          cliente: undefined,
          q: undefined,
          page: undefined,
        },
        false,
      )
    },
    [escribir],
  )

  /** La fecha vale para los tres modos: se elige una vez para toda la lista. */
  const cambiarFecha = useCallback(
    (fecha: string) => escribir({ fecha: fecha || undefined }, false),
    [escribir],
  )

  const cambiarBusqueda = useCallback(
    (q: string) =>
      // Reemplaza la entrada de historial, como el resto de buscadores: con un
      // rebote de 300 ms cada letra dejaria una entrada y deshacer una busqueda
      // costaria tantos "atras" como caracteres.
      // Y suelta al cliente elegido, que casi seguro ya no esta en la lista.
      escribir({ q: q || undefined, cliente: undefined, page: undefined }, true),
    [escribir],
  )

  const elegirCliente = useCallback(
    (contactId: string) => escribir({ cliente: contactId || undefined }, false),
    [escribir],
  )

  // Elegir dia o semana es una accion deliberada y merece su entrada de
  // historial, al contrario que teclear en el buscador. Y suelta la pagina: con
  // la 4 puesta, un martes de dos paginas saldria vacio sin explicar por que.
  const cambiarRuta = useCallback(
    (ruta: DiaRuta | '') => escribir({ ruta: ruta || undefined, page: undefined }, false),
    [escribir],
  )

  const cambiarSemana = useCallback(
    (semana: SemanaReparto | '') => escribir({ semana: semana || undefined, page: undefined }, false),
    [escribir],
  )

  const irAPagina = useCallback(
    (pagina: number) => escribir({ page: pagina <= 1 ? undefined : String(pagina) }, false),
    [escribir],
  )

  return {
    filtros,
    cambiarModo,
    cambiarFecha,
    cambiarBusqueda,
    elegirCliente,
    cambiarRuta,
    cambiarSemana,
    irAPagina,
  }
}
